import { each, entries } from "./general-utils.mjs";
import { includes, isArray, isBoolean, isNullish, isString, isUndefined, trim } from "@posthog/core";
import { logger } from "./logger.mjs";
import { getTargetingUrl } from "./url-targeting-utils.mjs";
import { isElementNode, isShadowRoot, isTag, isTextNode } from "./element-utils.mjs";
const MAX_DOM_ANCESTOR_DEPTH = 1000;
function splitClassString(s) {
    return s ? trim(s).split(/\s+/) : [];
}
function checkForURLMatches(urlsList, instance) {
    const url = getTargetingUrl(instance);
    return !!(url && urlsList && urlsList.some((regex)=>url.match(regex)));
}
function getClassNames(el) {
    let className = '';
    switch(typeof el.className){
        case 'string':
            className = el.className;
            break;
        case 'object':
            className = (el.className && 'baseVal' in el.className ? el.className.baseVal : null) || el.getAttribute('class') || '';
            break;
        default:
            className = '';
    }
    return splitClassString(className);
}
function makeSafeText(s) {
    if (isNullish(s)) return null;
    return trim(s).split(/(\s+)/).filter((s)=>shouldCaptureValue(s)).join('').replace(/[\r\n]/g, ' ').replace(/[ ]+/g, ' ').substring(0, 255);
}
function getSafeText(el) {
    let elText = '';
    if (shouldCaptureElement(el) && !isSensitiveElement(el) && el.childNodes && el.childNodes.length) each(el.childNodes, function(child) {
        if (isTextNode(child) && child.textContent) elText += makeSafeText(child.textContent) ?? '';
    });
    return trim(elText);
}
function getEventTarget(e) {
    if (isUndefined(e.target)) return e.srcElement || null;
    if (e.target?.shadowRoot) return e.composedPath()[0] || null;
    return e.target || null;
}
const autocaptureCompatibleElements = [
    'a',
    'button',
    'form',
    'input',
    'select',
    'textarea',
    'label'
];
function checkIfElementTreePassesElementAllowList(elements, autocaptureConfig) {
    const allowlist = autocaptureConfig?.element_allowlist;
    if (isUndefined(allowlist)) return true;
    for (const el of elements)if (allowlist.some((elementType)=>el.tagName.toLowerCase() === elementType)) return true;
    return false;
}
function elementMatchesCSSSelector(el, selector) {
    const matches = el.matches || el.matchesSelector || el.msMatchesSelector || el.mozMatchesSelector || el.webkitMatchesSelector || el.oMatchesSelector;
    try {
        return matches ? matches.call(el, selector) : false;
    } catch  {
        return false;
    }
}
function checkIfElementsMatchCSSSelector(elements, selectorList) {
    if (isUndefined(selectorList)) return true;
    for (const el of elements)if (selectorList.some((selector)=>elementMatchesCSSSelector(el, selector))) return true;
    return false;
}
function getParentElement(curEl) {
    const parentNode = curEl.parentNode;
    if (!parentNode || !isElementNode(parentNode)) return false;
    return parentNode;
}
const DEFAULT_AUTOCAPTURE_IGNORE_LIST = [
    '.ph-no-autocapture',
    '[data-ph-no-autocapture]'
];
const DEFAULT_CONTENT_IGNORELIST = [
    'next',
    'previous',
    'prev',
    '>',
    '<',
    '→',
    '←',
    '›',
    '‹',
    '»',
    '«',
    '▶',
    '◀',
    '❯',
    '❮'
];
const DEFAULT_CONTENT_IGNORELIST_WITH_STEPPERS = [
    ...DEFAULT_CONTENT_IGNORELIST,
    '+',
    '-',
    '−',
    '–'
];
const MAX_CONTENT_IGNORELIST_ENTRIES = DEFAULT_CONTENT_IGNORELIST_WITH_STEPPERS.length + 10;
const INTERACTIVE_TAGS = [
    'button',
    'a',
    'input',
    'select',
    'textarea',
    'label'
];
const INTERACTIVE_ROLES = [
    'button',
    'link',
    'tab',
    'menuitem',
    'option'
];
const matchesContentKeyword = (text, keyword)=>{
    if (!/[a-z0-9]/i.test(keyword)) return text === keyword;
    return includes(DEFAULT_CONTENT_IGNORELIST_WITH_STEPPERS, keyword) ? new RegExp(`\\b${keyword}\\b`).test(text) : text.includes(keyword);
};
function shouldIgnoreByContent(contentIgnorelist, { safeText, ariaLabel }) {
    let keywords;
    if (true === contentIgnorelist) keywords = DEFAULT_CONTENT_IGNORELIST;
    else {
        if (!isArray(contentIgnorelist)) return false;
        if (contentIgnorelist.length > MAX_CONTENT_IGNORELIST_ENTRIES) {
            logger.error(`[PostHog] content_ignorelist array cannot exceed ${MAX_CONTENT_IGNORELIST_ENTRIES} items. Use css_selector_ignorelist for more complex matching.`);
            return false;
        }
        keywords = contentIgnorelist.map((k)=>k.toLowerCase());
    }
    return keywords.some((keyword)=>matchesContentKeyword(safeText, keyword) || matchesContentKeyword(ariaLabel, keyword));
}
const isInteractiveElement = (el)=>includes(INTERACTIVE_TAGS, el.tagName.toLowerCase()) || includes(INTERACTIVE_ROLES, (el.getAttribute('role') || '').toLowerCase());
function clickedControlText(el, targetElementList) {
    for (const candidate of targetElementList)if (isInteractiveElement(candidate)) return controlLabelText(candidate);
    for (const candidate of targetElementList){
        try {
            if (window?.getComputedStyle(candidate).getPropertyValue('cursor') !== 'pointer') break;
        } catch  {
            break;
        }
        const label = controlLabelText(candidate);
        if (label.safeText || label.ariaLabel) return label;
    }
    return controlLabelText(el);
}
function controlLabelText(control) {
    let text = '';
    let firstAriaLabel = '';
    const collect = (node)=>{
        if (isElementNode(node) && shouldCaptureElement(node) && !isSensitiveElement(node)) {
            text += ` ${getSafeText(node)}`;
            firstAriaLabel = firstAriaLabel || node.getAttribute('aria-label') || '';
            each(node.childNodes, collect);
        }
    };
    collect(control);
    text = trim(text.replace(/\s+/g, ' '));
    const safeText = (shouldCaptureValue(text) ? text : '').toLowerCase();
    const ariaLabel = control.getAttribute('aria-label') || (safeText ? '' : firstAriaLabel);
    return {
        safeText,
        ariaLabel: trim(ariaLabel).toLowerCase()
    };
}
const DEFAULT_DEAD_CLICK_IGNORE_LIST = [
    '.ph-no-deadclick',
    '.ph-no-capture'
];
function shouldCaptureDeadClick(el, _config) {
    const win = "u" < typeof window ? void 0 : window;
    if (!win || cannotCheckForAutocapture(el)) return false;
    const selectorIgnoreList = isBoolean(_config) ? DEFAULT_DEAD_CLICK_IGNORE_LIST : _config?.css_selector_ignorelist ?? DEFAULT_DEAD_CLICK_IGNORE_LIST;
    const { targetElementList } = getElementAndParentsForElement(el, false);
    return !checkIfElementsMatchCSSSelector(targetElementList, selectorIgnoreList);
}
const DEFAULT_RAGE_CLICK_IGNORE_LIST = [
    '.ph-no-rageclick',
    '.ph-no-capture'
];
const TEXT_SELECTION_INPUT_TYPES = [
    '',
    'text',
    'search',
    'email',
    'password',
    'url',
    'tel',
    'number'
];
function isContentEditableTarget(el) {
    if (el.isContentEditable) return true;
    const contentEditable = el.getAttribute?.('contenteditable');
    return 'true' === contentEditable || '' === contentEditable;
}
function isTextSelectionTarget(el) {
    if (!el || !isElementNode(el)) return false;
    if (isTag(el, 'textarea')) return true;
    if (isTag(el, 'input')) return includes(TEXT_SELECTION_INPUT_TYPES, (el.getAttribute('type') || '').toLowerCase());
    return isContentEditableTarget(el);
}
function shouldCaptureRageclick(el, _config) {
    const win = "u" < typeof window ? void 0 : window;
    if (!win || cannotCheckForAutocapture(el)) return false;
    let selectorIgnoreList;
    let contentIgnorelist;
    let ignoreTextSelection;
    if (isBoolean(_config)) {
        selectorIgnoreList = _config ? DEFAULT_RAGE_CLICK_IGNORE_LIST : false;
        contentIgnorelist = void 0;
        ignoreTextSelection = false;
    } else {
        selectorIgnoreList = _config?.css_selector_ignorelist ?? DEFAULT_RAGE_CLICK_IGNORE_LIST;
        contentIgnorelist = _config?.content_ignorelist;
        ignoreTextSelection = _config?.ignore_text_selection ?? false;
    }
    if (false === selectorIgnoreList) return false;
    if (ignoreTextSelection && isTextSelectionTarget(el)) return false;
    const { targetElementList } = getElementAndParentsForElement(el, false);
    if (contentIgnorelist && shouldIgnoreByContent(contentIgnorelist, clickedControlText(el, targetElementList))) return false;
    return !checkIfElementsMatchCSSSelector(targetElementList, selectorIgnoreList);
}
const cannotCheckForAutocapture = (el)=>!el || isTag(el, 'html') || !isElementNode(el);
const getElementAndParentsForElement = (el, captureOnAnyElement)=>{
    const win = "u" < typeof window ? void 0 : window;
    if (!win || cannotCheckForAutocapture(el)) return {
        parentIsUsefulElement: false,
        targetElementList: []
    };
    let parentIsUsefulElement = false;
    const targetElementList = [
        el
    ];
    let curEl = el;
    while(curEl.parentNode && !isTag(curEl, 'body')){
        if (isShadowRoot(curEl.parentNode)) {
            targetElementList.push(curEl.parentNode.host);
            curEl = curEl.parentNode.host;
            continue;
        }
        const parentNode = getParentElement(curEl);
        if (!parentNode) break;
        if (captureOnAnyElement || autocaptureCompatibleElements.indexOf(parentNode.tagName.toLowerCase()) > -1) parentIsUsefulElement = true;
        else try {
            const compStyles = win.getComputedStyle(parentNode);
            if (compStyles && 'pointer' === compStyles.getPropertyValue('cursor')) parentIsUsefulElement = true;
        } catch  {}
        targetElementList.push(parentNode);
        curEl = parentNode;
    }
    return {
        parentIsUsefulElement,
        targetElementList
    };
};
function shouldSkipDeadClick(el) {
    const win = "u" < typeof window ? void 0 : window;
    if (!win || cannotCheckForAutocapture(el)) return false;
    const { targetElementList } = getElementAndParentsForElement(el, false);
    return targetElementList.some((node)=>isTag(node, 'a'));
}
function shouldCaptureDomEvent(el, event, autocaptureConfig, captureOnAnyElement, allowedEventTypes, instance) {
    const win = "u" < typeof window ? void 0 : window;
    if (!win || cannotCheckForAutocapture(el)) return false;
    if (autocaptureConfig?.url_allowlist) {
        if (!checkForURLMatches(autocaptureConfig.url_allowlist, instance)) return false;
    }
    if (autocaptureConfig?.url_ignorelist) {
        if (checkForURLMatches(autocaptureConfig.url_ignorelist, instance)) return false;
    }
    if (autocaptureConfig?.dom_event_allowlist) {
        const allowlist = autocaptureConfig.dom_event_allowlist;
        if (allowlist && !allowlist.some((eventType)=>event.type === eventType)) return false;
    }
    const { parentIsUsefulElement, targetElementList } = getElementAndParentsForElement(el, captureOnAnyElement);
    if (!checkIfElementTreePassesElementAllowList(targetElementList, autocaptureConfig)) return false;
    if (!checkIfElementsMatchCSSSelector(targetElementList, autocaptureConfig?.css_selector_allowlist)) return false;
    const selectorIgnoreList = autocaptureConfig?.css_selector_ignorelist ?? DEFAULT_AUTOCAPTURE_IGNORE_LIST;
    if (checkIfElementsMatchCSSSelector(targetElementList, selectorIgnoreList)) return false;
    try {
        const compStyles = win.getComputedStyle(el);
        if (compStyles && 'pointer' === compStyles.getPropertyValue('cursor') && 'click' === event.type) return true;
    } catch  {}
    const tag = el.tagName.toLowerCase();
    switch(tag){
        case 'html':
            return false;
        case 'form':
            return (allowedEventTypes || [
                'submit'
            ]).indexOf(event.type) >= 0;
        case 'input':
        case 'select':
        case 'textarea':
            return (allowedEventTypes || [
                'change',
                'click'
            ]).indexOf(event.type) >= 0;
        default:
            if (parentIsUsefulElement) return (allowedEventTypes || [
                'click'
            ]).indexOf(event.type) >= 0;
            return (allowedEventTypes || [
                'click'
            ]).indexOf(event.type) >= 0 && (autocaptureCompatibleElements.indexOf(tag) > -1 || 'true' === el.getAttribute('contenteditable'));
    }
}
function shouldCaptureElement(el) {
    const seen = new Set();
    let depth = 0;
    for(let curEl = el; curEl.parentNode && !isTag(curEl, 'body'); curEl = curEl.parentNode){
        if (depth++ >= MAX_DOM_ANCESTOR_DEPTH || seen.has(curEl)) return false;
        seen.add(curEl);
        const classes = getClassNames(curEl);
        if (includes(classes, 'ph-sensitive') || includes(classes, 'ph-no-capture')) return false;
    }
    if (includes(getClassNames(el), 'ph-include')) return true;
    const type = el.type || '';
    if (isString(type)) switch(type.toLowerCase()){
        case 'hidden':
            return false;
        case 'password':
            return false;
    }
    const name = el.name || el.id || '';
    if (isString(name)) {
        const sensitiveNameRegex = /^cc|cardnum|ccnum|creditcard|csc|cvc|cvv|exp|pass|pwd|routing|seccode|securitycode|securitynum|socialsec|socsec|ssn/i;
        if (sensitiveNameRegex.test(name.replace(/[^a-zA-Z0-9]/g, ''))) return false;
    }
    return true;
}
function isSensitiveElement(el) {
    const allowedInputTypes = [
        'button',
        'checkbox',
        'submit',
        'reset'
    ];
    if (isTag(el, 'input') && !allowedInputTypes.includes(el.type) || isTag(el, 'select') || isTag(el, 'textarea') || 'true' === el.getAttribute('contenteditable')) return true;
    return false;
}
const coreCCPattern = "(4[0-9]{12}(?:[0-9]{3})?)|(5[1-5][0-9]{14})|(6(?:011|5[0-9]{2})[0-9]{12})|(3[47][0-9]{13})|(3(?:0[0-5]|[68][0-9])[0-9]{11})|((?:2131|1800|35[0-9]{3})[0-9]{11})";
const anchoredCCRegex = new RegExp(`^(?:${coreCCPattern})$`);
const networkCCCandidateRegex = /(^|[^0-9A-Za-z_])([0-9][0-9 -]*[0-9])(?=$|[^0-9A-Za-z_])/g;
const networkCCLengths = [
    16,
    15,
    14,
    13
];
const coreSSNPattern = "\\d{3}-?\\d{2}-?\\d{4}";
const anchoredSSNRegex = new RegExp(`^(${coreSSNPattern})$`);
const networkSSNPattern = "(?!000|666)[0-9]{3}-?(?!00)[0-9]{2}-?(?!0000)[0-9]{4}";
const networkSSNCandidateRegex = new RegExp(`(^|[^0-9])(${networkSSNPattern})(?=$|([^0-9]))`, 'g');
const identifierCharacterRegex = /[0-9A-Za-z_]/;
function passesLuhnCheck(value) {
    let sum = 0;
    let shouldDouble = false;
    for(let i = value.length - 1; i >= 0; i--){
        let digit = value.charCodeAt(i) - 48;
        if (shouldDouble) {
            digit *= 2;
            if (digit > 9) digit -= 9;
        }
        sum += digit;
        shouldDouble = !shouldDouble;
    }
    return sum % 10 === 0;
}
function containsCreditCardNumber(value) {
    networkCCCandidateRegex.lastIndex = 0;
    let match;
    while(match = networkCCCandidateRegex.exec(value)){
        const matchedCandidate = match[2];
        if (!matchedCandidate) continue;
        const candidate = matchedCandidate.replace(/[- ]/g, '');
        for(let start = 0; start < candidate.length; start++)for (const length of networkCCLengths){
            const end = start + length;
            if (end <= candidate.length) {
                const possibleCardNumber = candidate.slice(start, end);
                if (anchoredCCRegex.test(possibleCardNumber) && passesLuhnCheck(possibleCardNumber)) return true;
            }
        }
    }
    return false;
}
function containsSSN(value) {
    networkSSNCandidateRegex.lastIndex = 0;
    let match;
    while(match = networkSSNCandidateRegex.exec(value)){
        const characterBefore = match[1];
        const characterAfter = match[3];
        if (characterBefore && characterAfter && identifierCharacterRegex.test(characterBefore) && identifierCharacterRegex.test(characterAfter)) continue;
        return true;
    }
    return false;
}
function shouldCaptureValue(value, anchorRegexes = true) {
    if (isNullish(value)) return false;
    if (isString(value)) {
        value = trim(value);
        const containsCreditCard = anchorRegexes ? anchoredCCRegex.test((value || '').replace(/[- ]/g, '')) : containsCreditCardNumber(value);
        if (containsCreditCard) return false;
        const containsSocialSecurityNumber = anchorRegexes ? anchoredSSNRegex.test(value) : containsSSN(value);
        if (containsSocialSecurityNumber) return false;
    }
    return true;
}
function isAngularStyleAttr(attributeName) {
    if (isString(attributeName)) return '_ngcontent' === attributeName.substring(0, 10) || '_nghost' === attributeName.substring(0, 7);
    return false;
}
function getDirectAndNestedSpanText(target) {
    let text = getSafeText(target);
    text = `${text} ${getNestedSpanText(target)}`.trim();
    return shouldCaptureValue(text) ? text : '';
}
function getNestedSpanText(target) {
    let text = '';
    if (target && target.childNodes && target.childNodes.length) each(target.childNodes, function(child) {
        if (child && child.tagName?.toLowerCase() === 'span') try {
            const spanText = getSafeText(child);
            text = `${text} ${spanText}`.trim();
            if (child.childNodes && child.childNodes.length) text = `${text} ${getNestedSpanText(child)}`.trim();
        } catch (e) {
            logger.error('[AutoCapture]', e);
        }
    });
    return text;
}
function getElementsChainString(elements) {
    if (!isArray(elements)) return '';
    return elementsToString(extractElements(elements));
}
function escapeQuotes(input) {
    return input.replace(/"|\\"/g, '\\"');
}
function lexicalCompare(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
function elementsToString(elements) {
    const ret = elements.map((element)=>{
        let el_string = '';
        if (element.tag_name) el_string += element.tag_name;
        if (element.attr_class) {
            element.attr_class.sort();
            for (const single_class of element.attr_class)el_string += `.${single_class.replace(/"/g, '')}`;
        }
        const attributes = {
            ...element.text ? {
                text: element.text
            } : {},
            'nth-child': element.nth_child ?? 0,
            'nth-of-type': element.nth_of_type ?? 0,
            ...element.href ? {
                href: element.href
            } : {},
            ...element.attr_id ? {
                attr_id: element.attr_id
            } : {},
            ...element.attributes
        };
        const sortedAttributes = {};
        entries(attributes).sort(([a], [b])=>lexicalCompare(a, b)).forEach(([key, value])=>sortedAttributes[escapeQuotes(key.toString())] = escapeQuotes(value.toString()));
        el_string += ':';
        el_string += entries(sortedAttributes).map(([key, value])=>`${key}="${value}"`).join('');
        return el_string;
    });
    return ret.join(';');
}
function extractElements(elements) {
    return elements.map((el)=>{
        const attributes = {};
        const response = {
            text: el['$el_text']?.slice(0, 400),
            tag_name: el['tag_name'],
            href: el['attr__href']?.slice(0, 2048),
            attr_class: extractAttrClass(el),
            attr_id: el['attr__id'],
            nth_child: el['nth_child'],
            nth_of_type: el['nth_of_type'],
            attributes
        };
        entries(el).filter(([key])=>0 === key.indexOf('attr__')).forEach(([key, value])=>response.attributes[key] = value);
        return response;
    });
}
function extractAttrClass(el) {
    const attr_class = el['attr__class'];
    if (!attr_class) return;
    if (isArray(attr_class)) return attr_class;
    return splitClassString(attr_class);
}
export { DEFAULT_AUTOCAPTURE_IGNORE_LIST, DEFAULT_CONTENT_IGNORELIST_WITH_STEPPERS, MAX_DOM_ANCESTOR_DEPTH, autocaptureCompatibleElements, elementMatchesCSSSelector, getClassNames, getDirectAndNestedSpanText, getElementsChainString, getEventTarget, getNestedSpanText, getParentElement, getSafeText, isAngularStyleAttr, isSensitiveElement, isTextSelectionTarget, makeSafeText, shouldCaptureDeadClick, shouldCaptureDomEvent, shouldCaptureElement, shouldCaptureRageclick, shouldCaptureValue, shouldSkipDeadClick, splitClassString };
